import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * BottomSheet
 *
 * Shared mobile-only bottom-sheet shell (backdrop + rounded-t-2xl slide-up
 * panel), matching the pattern already used by AppShell's MoreDrawer and
 * Header's status sheet. `lg:hidden` on the outer wrapper means this never
 * renders above the `lg` breakpoint, regardless of `open`.
 *
 * Rendered via createPortal into document.body (same technique already used
 * by the manual-trading confirm modals) so `position: fixed` here is always
 * relative to the true viewport — needed because, unlike MoreDrawer/Header's
 * sheet, this component can be invoked from deep inside page content that
 * AppShell wraps in an `.animate-fade-in` div. That wrapper's mount animation
 * leaves a permanent (identity) `transform` behind (`animation-fill-mode:
 * forwards`), which per the CSS spec becomes the containing block for any
 * `position: fixed` descendant — without the portal, this sheet would render
 * offset by that ancestor's box instead of pinned to the viewport.
 */
export default function BottomSheet({ open, onClose, title, children }) {
  if (!open) return null;

  return createPortal(
    <div className="lg:hidden fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-bg-card border-t border-bg-border rounded-t-2xl max-h-[82vh] flex flex-col animate-slide-up safe-area-pb">
        <div className="flex justify-center pt-2 pb-1 shrink-0" aria-hidden="true">
          <div className="w-9 h-1 rounded-full bg-bg-border" />
        </div>
        <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
          <span className="text-sm font-medium text-txt-primary truncate">{title}</span>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-11 h-11 -m-2.5 flex items-center justify-center rounded-lg hover:bg-bg-hover text-txt-muted shrink-0"
          >
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-4 py-2">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
